'use client'

import { memo, type FC } from 'react'
import { Controller, type Control, type FieldPath, type FieldValues } from 'react-hook-form'

import { FieldRenderer, type FieldRendererProps } from './FormFieldRenderer'
import { useDynamicFields } from '../hooks/useDynamicFields'
import { isChecklistField, validateChecklist } from '../lib/checklistHelper'
import type { DynamicField } from '../types'
import type { Any } from '@api/orvalFetcher'

interface DynamicFieldsProps<TFieldValues extends FieldValues> {
  fields?: Array<DynamicField>
  control: Control<TFieldValues, Any>
  fieldHelp?: Record<string, string>
  /**
   * Custom renderers keyed by `param_key` — swap the control for specific backend-driven
   * fields while keeping the backend's field order, labels, defaults, presence, and RHF
   * validation. Fields without an entry use the default `FieldRenderer`.
   *
   * Wrap the custom control in `FieldShell` (from `FormFieldRenderer`) so it gets the same
   * label / required mark / help popover / error row as generic fields.
   *
   * @example
   * // Render `window_size` as a slider; every other field stays on the default renderer.
   * const WindowSizeField: FC<FieldRendererProps> = ({ paramsField, field, error, helperText }) => (
   *   <FieldShell paramsField={paramsField} error={error} helperText={helperText}>
   *     <SliderWithInput min={1} max={20} value={Number(field.value) || 1} onChange={field.onChange} />
   *   </FieldShell>
   * )
   *
   * // Define the map at module level (stable identity), then:
   * <DynamicFields fields={fields} control={control} fieldOverrides={{ window_size: WindowSizeField }} />
   */
  fieldOverrides?: Record<string, FC<FieldRendererProps>>
}

const DynamicFieldsInner = <TFieldValues extends FieldValues>({
  fields,
  control,
  fieldHelp,
  fieldOverrides,
}: DynamicFieldsProps<TFieldValues>) => {
  const dynamicFields = useDynamicFields(control, fields)

  return (
    <>
      {dynamicFields?.map((paramsField) => {
        const helperText = fieldHelp?.[paramsField.param_key || '']

        if (paramsField.type === 'markdown' || paramsField.type === 'divider') {
          return (
            <FieldRenderer
              key={`${paramsField.param_key}-${paramsField.name}`}
              paramsField={paramsField}
              field={{ value: undefined, onChange: () => {} }}
              error={undefined}
              helperText={helperText}
            />
          )
        }

        const Renderer = fieldOverrides?.[paramsField.param_key || ''] ?? FieldRenderer

        return (
          <Controller
            key={paramsField.param_key}
            // Field names come from the backend action config, not the static form type.
            name={(paramsField.param_key || '') as FieldPath<TFieldValues>}
            control={control}
            rules={{
              required: paramsField.required,
              // Row caps and the total-vs-balance check must gate the submit button, not just paint red.
              ...(isChecklistField(paramsField) && { validate: validateChecklist(paramsField) }),
            }}
            render={({ field, fieldState }) => (
              <Renderer
                paramsField={paramsField}
                field={field}
                error={fieldState.error?.message}
                helperText={helperText}
              />
            )}
          />
        )
      })}
    </>
  )
}

const MemoDynamicFields = memo(DynamicFieldsInner)
MemoDynamicFields.displayName = 'DynamicFields'

export const DynamicFields = MemoDynamicFields as typeof DynamicFieldsInner
